import { z } from 'zod';

/**
 * Catalogue exhaustif des `link_type` entre parties (M068).
 *
 * Organisé en 6 catégories (`PartyLinkCategory`). Le catalogue est figé en
 * TypeScript (pas en DB) pour permettre l'extension sans migration : ajouter
 * un type = ajouter une clé ici, le validator Zod et le tool agent l'acceptent
 * automatiquement.
 *
 * Spec : docs/architecture/case-wiki/data-model.md §6.2.
 *
 * Chaque entrée porte :
 *   - `label` : libellé FR affiché en UI
 *   - `description` : tooltip pour l'avocat
 *   - `direction` : 'directed' (A→B) ou 'symmetric' (A↔B)
 *   - `applicable` : matières du droit où ce type de lien est pertinent
 *     (informationnel, ne contraint pas — l'agent peut l'utiliser hors matière)
 */

export const PartyLinkCategorySchema = z.enum([
  'personal',     // famille, mariage, parenté, garde, tutelle
  'contractual',  // bail, mandat, prêt, contrat
  'corporate',    // gérant, associé, filiale
  'patrimonial',  // caution, créancier, débiteur
  'procedural',   // intervention, représentation procédurale
  'litigation',   // adversaire, complice, victime
]);
export type PartyLinkCategory = z.infer<typeof PartyLinkCategorySchema>;

export const PartyLinkDirectionSchema = z.enum(['directed', 'symmetric']);
export type PartyLinkDirection = z.infer<typeof PartyLinkDirectionSchema>;

export const PartyLinkConfidenceSchema = z.enum(['high', 'medium', 'low']);
export type PartyLinkConfidence = z.infer<typeof PartyLinkConfidenceSchema>;

interface LinkTypeMeta {
  label: string;
  description: string;
  direction: PartyLinkDirection;
  /**
   * Matières du droit où ce lien est pertinent. Indicatif. La liste correspond
   * approximativement au champ `cases.procedure_type`.
   */
  applicable: ReadonlyArray<
    | 'civil' | 'penal' | 'social' | 'commercial' | 'famille' | 'public'
    | 'all'
  >;
}

/**
 * Catalogue par catégorie. Les clés sont les valeurs DB de `link_type`.
 * Convention : `<role>_de` (directed, A→B) ou `<role>_avec`/`<role>_a` pour
 * symétrique (A↔B).
 */
export const PARTY_LINK_TYPES = {
  personal: {
    epoux_de: { label: 'Époux de', description: 'Lien matrimonial.', direction: 'symmetric', applicable: ['famille', 'civil', 'penal'] },
    concubin_de: { label: 'Concubin de', description: 'Union libre.', direction: 'symmetric', applicable: ['famille', 'civil'] },
    pacse_avec: { label: 'Pacsé avec', description: 'Pacte civil de solidarité.', direction: 'symmetric', applicable: ['famille', 'civil'] },
    parent_de: { label: 'Parent de', description: 'Filiation directe.', direction: 'directed', applicable: ['famille', 'civil', 'penal'] },
    enfant_de: { label: 'Enfant de', description: 'Filiation directe inverse.', direction: 'directed', applicable: ['famille', 'civil', 'penal'] },
    frere_de: { label: 'Frère/sœur de', description: 'Fratrie.', direction: 'symmetric', applicable: ['famille', 'civil'] },
    oncle_de: { label: 'Oncle/tante de', description: 'Lien collatéral.', direction: 'directed', applicable: ['famille'] },
    cousin_de: { label: 'Cousin de', description: 'Lien collatéral.', direction: 'symmetric', applicable: ['famille'] },
    grand_parent_de: { label: 'Grand-parent de', description: 'Filiation seconde génération.', direction: 'directed', applicable: ['famille'] },
    petit_enfant_de: { label: 'Petit-enfant de', description: 'Filiation seconde génération inverse.', direction: 'directed', applicable: ['famille'] },
    beau_parent_de: { label: 'Beau-parent de', description: 'Filiation par alliance.', direction: 'directed', applicable: ['famille'] },
    gendre_de: { label: 'Gendre/belle-fille de', description: 'Alliance par mariage.', direction: 'directed', applicable: ['famille'] },
    tuteur_de: { label: 'Tuteur de', description: 'Représentation légale d\'un mineur ou majeur protégé.', direction: 'directed', applicable: ['famille', 'civil'] },
    curateur_de: { label: 'Curateur de', description: 'Assistance d\'un majeur protégé.', direction: 'directed', applicable: ['famille', 'civil'] },
    mandataire_familial_de: { label: 'Mandataire familial de', description: 'Mandat de protection future ou familial.', direction: 'directed', applicable: ['famille', 'civil'] },
    autre_personnel: { label: 'Autre lien personnel', description: 'Lien personnel non classé.', direction: 'symmetric', applicable: ['all'] },
  },
  contractual: {
    bailleur_de: { label: 'Bailleur de', description: 'Loue un bien à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    locataire_de: { label: 'Locataire de', description: 'Loue un bien depuis.', direction: 'directed', applicable: ['civil', 'commercial'] },
    acheteur_de: { label: 'Acheteur de', description: 'A acheté à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    vendeur_de: { label: 'Vendeur de', description: 'A vendu à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    donateur_de: { label: 'Donateur de', description: 'A fait donation à.', direction: 'directed', applicable: ['civil', 'famille'] },
    donataire_de: { label: 'Donataire de', description: 'A reçu donation de.', direction: 'directed', applicable: ['civil', 'famille'] },
    preteur_de: { label: 'Prêteur de', description: 'A prêté à (somme, bien).', direction: 'directed', applicable: ['civil', 'commercial'] },
    emprunteur_de: { label: 'Emprunteur de', description: 'A emprunté de.', direction: 'directed', applicable: ['civil', 'commercial'] },
    mandant_de: { label: 'Mandant de', description: 'Donne mandat à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    mandataire_de: { label: 'Mandataire de', description: 'Reçoit mandat de.', direction: 'directed', applicable: ['civil', 'commercial'] },
    franchiseur_de: { label: 'Franchiseur de', description: 'Concède une franchise.', direction: 'directed', applicable: ['commercial'] },
    franchise_de: { label: 'Franchisé de', description: 'Bénéficie d\'une franchise.', direction: 'directed', applicable: ['commercial'] },
    fournisseur_de: { label: 'Fournisseur de', description: 'Fournit biens/services à.', direction: 'directed', applicable: ['commercial'] },
    client_de: { label: 'Client de', description: 'Achète biens/services de.', direction: 'directed', applicable: ['commercial', 'civil'] },
    prestataire_de: { label: 'Prestataire de', description: 'Fournit prestation à.', direction: 'directed', applicable: ['commercial', 'civil'] },
    transporteur_de: { label: 'Transporteur de', description: 'Effectue le transport pour.', direction: 'directed', applicable: ['commercial'] },
    assureur_de: { label: 'Assureur de', description: 'Assure les risques de.', direction: 'directed', applicable: ['civil', 'commercial'] },
    assure_de: { label: 'Assuré de', description: 'Est assuré par.', direction: 'directed', applicable: ['civil', 'commercial'] },
    beneficiaire_de: { label: 'Bénéficiaire de', description: 'Tiers bénéficiaire d\'un contrat.', direction: 'directed', applicable: ['civil'] },
    souscripteur_de: { label: 'Souscripteur de', description: 'Souscrit un contrat au profit de.', direction: 'directed', applicable: ['civil'] },
    notaire_de: { label: 'Notaire de', description: 'Acte authentique reçu par.', direction: 'directed', applicable: ['civil', 'famille'] },
    expert_de: { label: 'Expert de', description: 'Expertise commandée par.', direction: 'directed', applicable: ['all'] },
    autre_contractuel: { label: 'Autre lien contractuel', description: 'Lien contractuel non classé.', direction: 'directed', applicable: ['all'] },
  },
  corporate: {
    gerant_de: { label: 'Gérant de', description: 'Dirigeant social statutaire.', direction: 'directed', applicable: ['commercial', 'penal'] },
    dirigeant_de: { label: 'Dirigeant de', description: 'Direction effective de la société.', direction: 'directed', applicable: ['commercial', 'penal'] },
    dirigeant_de_fait_de: { label: 'Dirigeant de fait de', description: 'Direction sans statut formel.', direction: 'directed', applicable: ['commercial', 'penal'] },
    president_de: { label: 'Président de', description: 'Président d\'organe.', direction: 'directed', applicable: ['commercial'] },
    directeur_general_de: { label: 'DG de', description: 'Directeur général.', direction: 'directed', applicable: ['commercial'] },
    administrateur_de: { label: 'Administrateur de', description: 'Membre du conseil d\'administration.', direction: 'directed', applicable: ['commercial'] },
    membre_cs_de: { label: 'Membre du CS de', description: 'Conseil de surveillance.', direction: 'directed', applicable: ['commercial'] },
    associe_de: { label: 'Associé de', description: 'Détient des parts/actions.', direction: 'directed', applicable: ['commercial'] },
    actionnaire_majoritaire_de: { label: 'Actionnaire majoritaire de', description: 'Détient > 50%.', direction: 'directed', applicable: ['commercial'] },
    actionnaire_minoritaire_de: { label: 'Actionnaire minoritaire de', description: 'Détient < 50%.', direction: 'directed', applicable: ['commercial'] },
    filiale_de: { label: 'Filiale de', description: 'Société contrôlée par.', direction: 'directed', applicable: ['commercial'] },
    societe_mere_de: { label: 'Société mère de', description: 'Société qui contrôle.', direction: 'directed', applicable: ['commercial'] },
    holding_de: { label: 'Holding de', description: 'Société de portefeuille.', direction: 'directed', applicable: ['commercial'] },
    succursale_de: { label: 'Succursale de', description: 'Établissement secondaire.', direction: 'directed', applicable: ['commercial'] },
    commissaire_aux_comptes_de: { label: 'CAC de', description: 'Commissaire aux comptes.', direction: 'directed', applicable: ['commercial'] },
    expert_comptable_de: { label: 'Expert-comptable de', description: 'Expert-comptable mandaté.', direction: 'directed', applicable: ['commercial'] },
    liquidateur_de: { label: 'Liquidateur de', description: 'Mandataire de liquidation.', direction: 'directed', applicable: ['commercial'] },
    mandataire_judiciaire_de: { label: 'Mandataire judiciaire de', description: 'Représentant des créanciers.', direction: 'directed', applicable: ['commercial'] },
    administrateur_judiciaire_de: { label: 'Administrateur judiciaire de', description: 'Procédure collective.', direction: 'directed', applicable: ['commercial'] },
    conciliateur_de: { label: 'Conciliateur de', description: 'Procédure de conciliation.', direction: 'directed', applicable: ['commercial'] },
    autre_corporate: { label: 'Autre lien corporate', description: 'Lien corporate non classé.', direction: 'directed', applicable: ['commercial'] },
  },
  patrimonial: {
    creancier_de: { label: 'Créancier de', description: 'A une créance sur.', direction: 'directed', applicable: ['civil', 'commercial'] },
    debiteur_de: { label: 'Débiteur de', description: 'Doit une somme à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    caution_de: { label: 'Caution de', description: 'Cautionne la dette de.', direction: 'directed', applicable: ['civil', 'commercial'] },
    garant_de: { label: 'Garant de', description: 'Garantit l\'obligation de.', direction: 'directed', applicable: ['civil', 'commercial'] },
    cocontractant_solidaire_de: { label: 'Cocontractant solidaire de', description: 'Solidarité contractuelle.', direction: 'symmetric', applicable: ['civil', 'commercial'] },
    cooblige_de: { label: 'Coobligé de', description: 'Obligation conjointe ou solidaire.', direction: 'symmetric', applicable: ['civil', 'commercial'] },
    cessionnaire_de: { label: 'Cessionnaire de', description: 'A reçu cession.', direction: 'directed', applicable: ['civil', 'commercial'] },
    cedant_de: { label: 'Cédant de', description: 'A cédé à.', direction: 'directed', applicable: ['civil', 'commercial'] },
    autre_patrimonial: { label: 'Autre lien patrimonial', description: 'Lien patrimonial non classé.', direction: 'directed', applicable: ['civil', 'commercial'] },
  },
  procedural: {
    avocat_de: { label: 'Avocat de', description: 'Conseil constitué pour.', direction: 'directed', applicable: ['all'] },
    huissier_de: { label: 'Huissier de', description: 'Acte signifié par.', direction: 'directed', applicable: ['all'] },
    expert_judiciaire_de: { label: 'Expert judiciaire de', description: 'Expertise désignée.', direction: 'directed', applicable: ['all'] },
    temoin_de: { label: 'Témoin de', description: 'A témoigné concernant.', direction: 'directed', applicable: ['penal', 'civil'] },
    juge_d_instruction_de: { label: 'Juge d\'instruction de', description: 'Magistrat instructeur.', direction: 'directed', applicable: ['penal'] },
    procureur_de: { label: 'Procureur de', description: 'Représente le ministère public.', direction: 'directed', applicable: ['penal'] },
    representant_legal_de: { label: 'Représentant légal de', description: 'Représentation légale (mineur, société).', direction: 'directed', applicable: ['all'] },
    intervenant_volontaire_de: { label: 'Intervenant volontaire dans', description: 'Intervention volontaire à la procédure.', direction: 'directed', applicable: ['civil'] },
    intervenant_force_de: { label: 'Intervenant forcé dans', description: 'Mis en cause dans la procédure.', direction: 'directed', applicable: ['civil'] },
    appele_en_garantie_par: { label: 'Appelé en garantie par', description: 'Appel en garantie.', direction: 'directed', applicable: ['civil'] },
    requerant_de: { label: 'Requérant à', description: 'Requérant dans la procédure.', direction: 'directed', applicable: ['public', 'civil'] },
    appelant_de: { label: 'Appelant contre', description: 'A interjeté appel contre.', direction: 'directed', applicable: ['all'] },
    intime_par: { label: 'Intimé par', description: 'Visé par un appel.', direction: 'directed', applicable: ['all'] },
    demandeur_au_pourvoi_de: { label: 'Demandeur au pourvoi de', description: 'A formé pourvoi.', direction: 'directed', applicable: ['all'] },
    defendeur_au_pourvoi_de: { label: 'Défendeur au pourvoi de', description: 'Visé par pourvoi.', direction: 'directed', applicable: ['all'] },
    autre_procedural: { label: 'Autre lien procédural', description: 'Rôle procédural non classé.', direction: 'directed', applicable: ['all'] },
  },
  litigation: {
    adversaire_de: { label: 'Adversaire de', description: 'Partie adverse au litige.', direction: 'symmetric', applicable: ['all'] },
    co_demandeur_de: { label: 'Co-demandeur de', description: 'Co-demande conjointe.', direction: 'symmetric', applicable: ['civil'] },
    co_defendeur_de: { label: 'Co-défendeur de', description: 'Co-défense conjointe.', direction: 'symmetric', applicable: ['civil'] },
    co_prevenu_de: { label: 'Co-prévenu de', description: 'Procédure pénale conjointe.', direction: 'symmetric', applicable: ['penal'] },
    auteur_principal_contre: { label: 'Auteur principal contre', description: 'Auteur principal de l\'infraction visant.', direction: 'directed', applicable: ['penal'] },
    complice_de: { label: 'Complice de', description: 'Complicité dans l\'infraction.', direction: 'directed', applicable: ['penal'] },
    recel_au_profit_de: { label: 'Receleur au profit de', description: 'Recel de l\'infraction de.', direction: 'directed', applicable: ['penal'] },
    victime_de: { label: 'Victime de', description: 'Victime directe.', direction: 'directed', applicable: ['penal', 'civil'] },
    victime_par_ricochet_de: { label: 'Victime par ricochet de', description: 'Préjudice indirect.', direction: 'directed', applicable: ['penal', 'civil'] },
    auteur_de_violence_contre: { label: 'Auteur de violence contre', description: 'Violence physique ou morale.', direction: 'directed', applicable: ['penal'] },
    auteur_de_concurrence_deloyale_contre: { label: 'Concurrence déloyale contre', description: 'Acte de concurrence déloyale.', direction: 'directed', applicable: ['commercial'] },
    auteur_de_rupture_brutale_contre: { label: 'Rupture brutale contre', description: 'Rupture brutale relations commerciales établies.', direction: 'directed', applicable: ['commercial'] },
    auteur_d_inexecution_contre: { label: 'Inexécution contre', description: 'Inexécution contractuelle.', direction: 'directed', applicable: ['civil', 'commercial'] },
    auteur_de_dol_contre: { label: 'Dol contre', description: 'Manœuvres dolosives.', direction: 'directed', applicable: ['civil'] },
    autre_litigieux: { label: 'Autre lien litigieux', description: 'Rôle litigieux non classé.', direction: 'directed', applicable: ['all'] },
  },
} as const;

/** Liste plate des `link_type` valides — source de l'enum Zod ci-dessous. */
const ALL_LINK_TYPES = Object.values(PARTY_LINK_TYPES).flatMap((cat) => Object.keys(cat));

function nonEmpty(values: string[]): [string, ...string[]] {
  const [first, ...rest] = values;
  if (first === undefined) throw new Error('PARTY_LINK_TYPES est vide : aucun link_type à proposer');
  return [first, ...rest];
}

/** Valeur reçue telle qu'elle est redite dans le refus — bornée, le message est rejoué à chaque tour. */
function describeReceivedLinkType(received: unknown): string {
  if (received === undefined || received === null) return '(champ absent)';
  const text = typeof received === 'string' ? received : JSON.stringify(received) ?? String(received);
  return `« ${text.length > 80 ? `${text.slice(0, 80)}…` : text} »`;
}

/**
 * Validator Zod du `link_type`. Liste fermée — l'agent ne peut pas inventer
 * une valeur (sécurité produit). Pour ajouter un type : éditer
 * `PARTY_LINK_TYPES` ci-dessus, le validator suit automatiquement.
 *
 * C'est un `z.enum`, pas un `z.string().refine(...)` : seul un enum est traduit en liste de
 * valeurs dans le schéma JSON de l'outil que reçoit le modèle. Avec le `refine`, l'agent ne voyait
 * qu'un champ texte et devinait — 18 135 refus en 7 jours de production (relevé du 2026-10-09 :
 * 94,8 % des appels en lint, 81 % en passe finale, 20,6 % en ingestion).
 */
export const PartyLinkTypeSchema = z.enum(nonEmpty(ALL_LINK_TYPES), {
  // Le message redit la valeur reçue : l'agent qui enchaîne plusieurs liens doit savoir lequel
  // de ses appels est refusé, et voir sa propre faute de frappe.
  errorMap: (_issue, ctx) => ({
    message: `link_type inconnu ${describeReceivedLinkType(ctx.data)} : choisis une des valeurs listées pour ce champ, dans la catégorie de la relation (catalogue dans la description du tool)`,
  }),
});
export type PartyLinkType = z.infer<typeof PartyLinkTypeSchema>;

/**
 * Catalogue des `link_type` par catégorie, tel qu'il est écrit dans la description de l'outil
 * `link_party_to_party`. Généré depuis `PARTY_LINK_TYPES` : une copie écrite à la main dans un
 * prompt avait dérivé jusqu'à annoncer 38 valeurs que l'outil refusait.
 */
export function renderPartyLinkCatalogue(): string {
  return Object.entries(PARTY_LINK_TYPES)
    .map(([category, types]) => {
      const entries = Object.entries(types as Record<string, LinkTypeMeta>).map(
        ([key, meta]) => `${key}${meta.direction === 'symmetric' ? ' (symétrique)' : ''}`,
      );
      return `${category} : ${entries.join(', ')}`;
    })
    .join(' | ');
}

/**
 * Pose `category` et `direction` d'après le catalogue : les deux se déduisent du `link_type`.
 * Les laisser à l'agent ouvrait deux façons de se tromper sans rien apporter (un `epoux_de`
 * rangé en `contractual`, ou déclaré orienté), et aucune couche ne les contrôlait. `category`
 * est donc facultative à l'entrée et toujours présente à la sortie.
 * Une `direction` fournie par l'agent est conservée : il peut connaître le sens d'une relation
 * que le catalogue donne orientée par défaut.
 *
 * @throws si `link_type` est hors catalogue — impossible après `PartyLinkTypeSchema`, qui le
 * refuse avant ; un appelant qui contourne le schéma doit échouer ici plutôt qu'écrire un lien
 * sans catégorie.
 */
export function alignPartyLinkWithCatalogue<
  T extends { link_type: string; category?: PartyLinkCategory; direction?: PartyLinkDirection },
>(payload: T): Omit<T, 'category'> & { category: PartyLinkCategory } {
  const category = getCategoryForLinkType(payload.link_type);
  if (category === null) {
    throw new Error(
      `alignPartyLinkWithCatalogue: link_type "${payload.link_type}" absent de PARTY_LINK_TYPES — valider le payload avec LinkPartyToPartyPayloadSchema avant d'aligner`,
    );
  }
  const types: Record<string, LinkTypeMeta> = PARTY_LINK_TYPES[category];
  return { ...payload, category, direction: payload.direction ?? types[payload.link_type]?.direction };
}

/** Helper : récupère la catégorie d'un link_type. */
export function getCategoryForLinkType(linkType: string): PartyLinkCategory | null {
  for (const [cat, types] of Object.entries(PARTY_LINK_TYPES)) {
    if (linkType in types) return cat as PartyLinkCategory;
  }
  return null;
}

/** Helper : récupère les link_types applicables à une matière. */
export function getLinkTypesForMatiere(
  matiere: 'civil' | 'penal' | 'social' | 'commercial' | 'famille' | 'public',
): Array<{ category: PartyLinkCategory; type: string; meta: LinkTypeMeta }> {
  const result: Array<{ category: PartyLinkCategory; type: string; meta: LinkTypeMeta }> = [];
  for (const [cat, types] of Object.entries(PARTY_LINK_TYPES)) {
    for (const [type, meta] of Object.entries(types)) {
      const m = meta as LinkTypeMeta;
      if (m.applicable.includes(matiere) || m.applicable.includes('all')) {
        result.push({ category: cat as PartyLinkCategory, type, meta: m });
      }
    }
  }
  return result;
}
